import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Megaphone, Music2, Youtube, Sparkles, Gift, MessageCircle, Check, Upload, X, Zap, TrendingUp, ListMusic } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { createMarketingRequest, getMarketingEligibility, uploadEpkImage, resolveAsset } from "@/lib/afmApi";
import { SectionTitle } from "@/components/common";

const WA_NUMBER = "584222002957";
const inputCls = "w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm outline-none focus:border-black";

const SERVICES = [
  { key: "TikTok ADS", icon: Zap, desc: "Anuncios pagados en TikTok para llevar tu tema a nuevas audiencias." },
  { key: "TikTok Lyrics", icon: Music2, desc: "Videos de letra (lyrics) optimizados para viralizar en TikTok." },
  { key: "TikTok Talents", icon: Sparkles, desc: "Creadores e influencers usando tu tema en sus videos." },
  { key: "YouTube ADS", icon: Youtube, desc: "Campañas de anuncios en YouTube para views y suscriptores." },
  { key: "YouTube Algorithm", icon: TrendingUp, desc: "Optimización para que el algoritmo recomiende tu video." },
  { key: "Spotify Playlisting", icon: ListMusic, desc: "Pitching a playlists editoriales e independientes de Spotify." },
];

const PAY = [
  { key: "pago_movil", label: "Pago Móvil", lines: ["Banco: Banesco", "Teléfono: 04142335584", "Cédula / RIF: 27279118"] },
  { key: "paypal", label: "PayPal", lines: ["hugopernia21@gmail.com"] },
  { key: "binance", label: "Binance Pay", lines: ["fulgencioantonio50@gmail.com"] },
];

const OBJECTIVES = ["Más streams en Spotify", "Más vistas en YouTube", "Crecer seguidores", "Viralizar en TikTok", "Pre-saves / Pre-order", "Entrar a playlists", "Más oyentes mensuales", "Branding / reconocimiento"];
const AUD_COUNTRIES = ["Venezuela", "Colombia", "México", "USA (Latinos)", "España", "Argentina", "Chile", "Perú", "Toda Latinoamérica"];
const AUD_AGES = ["13-17", "18-24", "25-34", "35-44", "45+"];
const AUD_STYLES = ["Urbano / Reggaetón", "Trap", "Pop", "Rock", "Electrónica", "Regional", "Salsa / Tropical", "Indie / Alternativo"];

function ChipGroup({ options, value, onToggle, testid }) {
  return (
    <div className="flex flex-wrap gap-2" data-testid={testid}>
      {options.map((o) => {
        const on = value.includes(o);
        return (
          <button key={o} type="button" data-testid={`${testid}-${o.replace(/[^a-zA-Z0-9]+/g, "-").toLowerCase()}`} onClick={() => onToggle(o)}
            className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${on ? "border-[#AC3333] bg-[#AC3333] text-white" : "border-slate-300 text-slate-600 hover:border-black dark:border-white/15 dark:text-slate-300"}`}>
            {on && <Check className="mr-1 inline h-3 w-3" />}{o}
          </button>
        );
      })}
    </div>
  );
}

export default function Marketing() {
  const { user } = useAuth();
  const [credit, setCredit] = useState(0);
  const [selected, setSelected] = useState([]);
  const [amount, setAmount] = useState(20);
  const [overBudget, setOverBudget] = useState(false);
  const [title, setTitle] = useState("");
  const [objectives, setObjectives] = useState([]);
  const [audCountries, setAudCountries] = useState([]);
  const [audAges, setAudAges] = useState([]);
  const [audStyles, setAudStyles] = useState([]);
  const [links, setLinks] = useState("");
  const [notes, setNotes] = useState("");
  const [method, setMethod] = useState("pago_movil");
  const [reference, setReference] = useState("");
  const [proof, setProof] = useState("");
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getMarketingEligibility(user.id).then((d) => setCredit(d?.welcome_credit_usd || 0)).catch(() => setCredit(0));
  }, [user.id]);

  const toggle = (k) => setSelected((s) => (s.includes(k) ? s.filter((x) => x !== k) : [...s, k]));
  const tog = (setter) => (v) => setter((s) => (s.includes(v) ? s.filter((x) => x !== v) : [...s, v]));
  const audienceStr = () => {
    const parts = [];
    if (audCountries.length) parts.push(`Países: ${audCountries.join(", ")}`);
    if (audAges.length) parts.push(`Edad: ${audAges.join(", ")}`);
    if (audStyles.length) parts.push(`Estilo: ${audStyles.join(", ")}`);
    return parts.join(" · ");
  };
  const netUsd = Math.max(amount - credit, 0);
  const selectedPay = PAY.find((p) => p.key === method);

  const waLink = () => {
    const svc = selected.length ? selected.join(", ") : "campaña de marketing";
    const msg = `Hola, soy ${user.name || user.email || "artista"} de Always Fresh. Quiero invertir más de $500 en ${svc}${title ? ` para mi tema "${title}"` : ""}. ¿Me ayudas a armar la campaña?`;
    return `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(msg)}`;
  };

  const uploadProof = async (file) => {
    if (!file) return; setUploading(true);
    try {
      const b64 = await new Promise((res) => { const r = new FileReader(); r.onload = () => res(r.result.split(",")[1]); r.readAsDataURL(file); });
      const up = await uploadEpkImage(user.id, file.type, b64);
      setProof(resolveAsset(up?.url));
    } catch { toast.error("No se pudo subir el comprobante"); } finally { setUploading(false); }
  };

  const confirm = async () => {
    if (!selected.length) { toast.error("Selecciona al menos un servicio"); return; }
    if (!objectives.length) { toast.error("Elige al menos un objetivo de la campaña"); return; }
    if (!reference.trim()) { toast.error("El número de referencia es obligatorio"); return; }
    if (!proof) { toast.error("Adjunta la captura del pago"); return; }
    setSaving(true);
    try {
      await createMarketingRequest({
        user_id: user.id, user_name: user.name || "", user_email: user.email || "",
        release_title: title.trim(), service: selected.join(" + "), budget_tier: `$${amount}`,
        objective: objectives.join(", "), audience: audienceStr(), links: links.trim(), notes: notes.trim(),
        payment_method: method, payment_reference: reference.trim(), payment_proof_url: proof,
      });
      toast.success("¡Campaña solicitada! Verificaremos tu pago y arrancamos.");
      setSelected([]); setAmount(20); setTitle(""); setObjectives([]); setAudCountries([]); setAudAges([]); setAudStyles([]); setLinks(""); setNotes(""); setReference(""); setProof("");
      getMarketingEligibility(user.id).then((d) => setCredit(d?.welcome_credit_usd || 0)).catch(() => {});
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo enviar la solicitud"); } finally { setSaving(false); }
  };

  return (
    <div className="space-y-6" data-testid="marketing-page">
      <SectionTitle icon={Megaphone} title="Marketing & Promoción" desc="Impulsa tus temas nuevos y los de tu catálogo. Arma tu campaña a tu medida." />

      {/* Hero */}
      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-gradient-to-br from-black to-[#2a0b0b] p-6 text-white sm:p-8">
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-white/60"><Megaphone className="h-4 w-4" /> Always Fresh Ads</div>
        <h2 className="mt-3 font-serif text-2xl font-bold sm:text-3xl">Haz sonar tu música donde importa</h2>
        <p className="mt-2 max-w-2xl text-sm text-white/70">Campañas para <strong className="text-white">temas nuevos y de catálogo</strong>: TikTok, YouTube y Spotify. Tú eliges cuánto invertir, nosotros lo ejecutamos.</p>
        {credit > 0 && (
          <div data-testid="marketing-welcome-credit" className="mt-5 inline-flex items-center gap-2 rounded-xl border border-emerald-400/40 bg-emerald-500/15 px-4 py-2.5 text-sm font-semibold text-emerald-200">
            <Gift className="h-4 w-4" /> Tienes ${credit} de regalo para tu primera campaña — se descuenta automáticamente.
          </div>
        )}
      </div>

      {/* Services */}
      <div>
        <h3 className="mb-3 font-serif text-lg font-bold text-black">Elige tus servicios</h3>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {SERVICES.map((s) => {
            const on = selected.includes(s.key);
            return (
              <button key={s.key} data-testid={`mkt-svc-${s.key.replace(/\s+/g, "-").toLowerCase()}`} onClick={() => toggle(s.key)}
                className={`flex flex-col items-start gap-2 rounded-xl border p-4 text-left transition-all ${on ? "border-[#AC3333] bg-[#AC3333]/5 ring-1 ring-[#AC3333]" : "border-slate-200 bg-white hover:border-black"}`}>
                <div className="flex w-full items-center justify-between">
                  <span className={`flex h-9 w-9 items-center justify-center rounded-lg ${on ? "bg-[#AC3333] text-white" : "bg-slate-100 text-slate-600"}`}><s.icon className="h-5 w-5" /></span>
                  {on && <span className="flex h-5 w-5 items-center justify-center rounded-full bg-[#AC3333] text-white"><Check className="h-3 w-3" /></span>}
                </div>
                <div className="font-serif text-base font-bold text-black">{s.key}</div>
                <p className="text-xs leading-relaxed text-slate-500">{s.desc}</p>
              </button>
            );
          })}
        </div>
      </div>

      {/* Arma tu campaña */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6" data-testid="marketing-builder">
        <div className="flex items-center gap-2"><Sparkles className="h-5 w-5 text-[#AC3333]" /><h3 className="font-serif text-lg font-bold text-black">Arma tu campaña · Solicita ahora</h3></div>

        <div className="mt-5 space-y-5">
          <div><label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-600">Tema a promocionar (opcional)</label><input data-testid="mkt-title" value={title} onChange={(e) => setTitle(e.target.value)} className={inputCls} placeholder="Nombre del tema nuevo o de tu catálogo" /></div>

          {/* Investment slider */}
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
            <div className="flex items-center justify-between">
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-600">¿Cuánto quieres invertir?</label>
              <span data-testid="mkt-amount-value" className="font-serif text-xl font-bold text-black">{overBudget ? "+$500" : `$${amount}`}</span>
            </div>
            <input data-testid="mkt-amount-slider" type="range" min={20} max={500} step={20} value={amount} disabled={overBudget}
              onChange={(e) => setAmount(Number(e.target.value))}
              className="mt-3 w-full accent-[#AC3333] disabled:opacity-40" />
            <div className="mt-1 flex justify-between text-[11px] text-slate-400"><span>$20</span><span>$500</span></div>

            <label className="mt-3 flex cursor-pointer items-center gap-2 text-sm text-slate-700" data-testid="mkt-over-toggle">
              <input type="checkbox" checked={overBudget} onChange={(e) => setOverBudget(e.target.checked)} className="h-4 w-4 accent-[#AC3333]" />
              Quiero invertir más de $500
            </label>

            {!overBudget && (
              <div className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t border-slate-200 pt-3 text-sm">
                <span className="text-slate-600">Inversión ${amount}{credit > 0 && <span className="font-semibold text-emerald-600"> · bono -${credit}</span>}</span>
                <span className="font-serif text-lg font-bold text-black">Total: <span data-testid="mkt-net-total">${netUsd}</span></span>
              </div>
            )}
          </div>

          {overBudget ? (
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-5 text-center" data-testid="mkt-advisor">
              <MessageCircle className="mx-auto h-8 w-8 text-emerald-600" />
              <p className="mt-2 font-serif text-base font-bold text-black">Para presupuestos de más de $500</p>
              <p className="mt-1 text-sm text-slate-600">Hablemos directo para armar una campaña a tu medida con un asesor.</p>
              <a data-testid="mkt-whatsapp" href={waLink()} target="_blank" rel="noreferrer" className="mt-4 inline-flex items-center gap-2 rounded-lg bg-[#25D366] px-5 py-2.5 text-sm font-semibold text-white hover:brightness-95">
                <MessageCircle className="h-4 w-4" /> Consultar por WhatsApp
              </a>
            </div>
          ) : (
            <>
              {/* Brief */}
              <div><label className="mb-2 block text-xs font-semibold uppercase tracking-wider text-slate-600">Objetivo de la campaña <span className="text-[#AC3333]">*</span></label><ChipGroup testid="mkt-objective" options={OBJECTIVES} value={objectives} onToggle={tog(setObjectives)} /></div>
              <div className="space-y-3 rounded-xl border border-slate-200 bg-slate-50 p-4 dark:border-white/10">
                <div className="text-xs font-semibold uppercase tracking-wider text-slate-600">Público objetivo</div>
                <div><div className="mb-1.5 text-[11px] font-medium text-slate-500">País / Región</div><ChipGroup testid="mkt-aud-country" options={AUD_COUNTRIES} value={audCountries} onToggle={tog(setAudCountries)} /></div>
                <div><div className="mb-1.5 text-[11px] font-medium text-slate-500">Edad</div><ChipGroup testid="mkt-aud-age" options={AUD_AGES} value={audAges} onToggle={tog(setAudAges)} /></div>
                <div><div className="mb-1.5 text-[11px] font-medium text-slate-500">Estilo / Audiencia</div><ChipGroup testid="mkt-aud-style" options={AUD_STYLES} value={audStyles} onToggle={tog(setAudStyles)} /></div>
              </div>
              <div><label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-600">Enlaces</label><textarea data-testid="mkt-links" value={links} onChange={(e) => setLinks(e.target.value)} rows={2} className={`${inputCls} resize-none`} placeholder="Link del release (Spotify/YouTube) y tus redes" /></div>
              <div><label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-600">Notas (opcional)</label><textarea data-testid="mkt-notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className={`${inputCls} resize-none`} placeholder="Cualquier detalle que debamos saber" /></div>

              {/* Payment */}
              <div className="flex gap-2">{PAY.map((p) => (<button key={p.key} data-testid={`mkt-pay-${p.key}`} onClick={() => setMethod(p.key)} className={`flex-1 rounded-lg border px-3 py-2 text-xs font-semibold ${method === p.key ? "border-black bg-black text-white" : "border-slate-300 text-slate-600 hover:border-black"}`}>{p.label}</button>))}</div>
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-4"><div className="mb-1 text-xs font-semibold uppercase tracking-wider text-slate-500">{selectedPay?.label}</div>{selectedPay?.lines.map((l) => <div key={l} className="font-mono text-sm text-black">{l}</div>)}</div>
              <div><label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-600">Número de Referencia <span className="text-[#AC3333]">*</span></label><input data-testid="mkt-reference" value={reference} onChange={(e) => setReference(e.target.value)} className={inputCls} placeholder="Ej. 0123456789" /></div>
              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-600">Captura del pago <span className="text-[#AC3333]">*</span></label>
                {proof ? (
                  <div className="relative inline-block"><img src={proof} alt="comprobante" className="h-24 rounded-lg border border-slate-200 object-cover" /><button onClick={() => setProof("")} className="absolute right-1 top-1 rounded-full bg-black/60 p-0.5 text-white"><X className="h-3 w-3" /></button></div>
                ) : (
                  <label data-testid="mkt-proof-upload" className="flex h-24 w-full cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-slate-300 text-slate-400 hover:border-black">{uploading ? <span className="h-5 w-5 animate-spin rounded-full border-2 border-slate-300 border-t-black" /> : <><Upload className="h-5 w-5" /><span className="mt-1 text-xs">Sube la captura</span></>}<input type="file" accept="image/png,image/jpeg" className="hidden" onChange={(e) => uploadProof(e.target.files?.[0])} /></label>
                )}
              </div>

              <button data-testid="marketing-confirm" onClick={confirm} disabled={saving} className="flex w-full items-center justify-center gap-2 rounded-lg bg-[#AC3333] px-5 py-3 text-sm font-semibold text-white hover:bg-[#FF0505] disabled:opacity-60">{saving ? <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" /> : <Check className="h-4 w-4" />} Solicitar campaña{credit > 0 ? ` (pagas $${netUsd})` : ""}</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
